// A location's wall clock, both ways (labor edits and displays; bugs B7/B8, T6 FIX). Every time a person reads or types
// is the location's wall clock, never the viewer's: an instant is shown as the (date, time) the location's clock read,
// and a typed (date, time) is resolved in the location's zone with the CD-B05 policy (a time in a spring-forward gap
// takes the first instant after the gap; a time in the repeated fall-back hour takes its first occurrence).
import { formatLocalDate, localDateToDays } from './calendar.js';
import { resolveLocal, toEpochMs, toLocal, type IanaZone } from './zone.js';
import { err, ok, type InstantInput, type LocalDate, type Result } from './types.js';

declare const brand: unique symbol;
/** A wall-clock time of day with seconds, 'HH:mm:ss' (00:00:00-23:59:59). */
export type WallTime = string & { readonly [brand]: 'WallTime' };

/** What the location's clock read at an instant. */
export type WallClock = {
  readonly date: LocalDate;
  readonly time: WallTime;
  /** Minutes east of UTC in effect at the instant (-420 for Pacific daylight time). */
  readonly offsetMinutes: number;
};

const pad = (n: number): string => String(n).padStart(2, '0');
const WALL_TIME = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;

/** 'HH:mm' or 'HH:mm:ss' to seconds since midnight. */
export function parseWallTime(text: string): Result<number, 'invalid_wall_time'> {
  const m = WALL_TIME.exec(text);
  if (m === null) return err('invalid_wall_time');
  const [h, min, s] = [Number(m[1]), Number(m[2]), Number(m[3] ?? '0')];
  return h > 23 || min > 59 || s > 59 ? err('invalid_wall_time') : ok(h * 3600 + min * 60 + s);
}

/** The location's wall clock at an instant (milliseconds are dropped, never rounded up). */
export function wallClockAt(at: InstantInput, zone: IanaZone): WallClock {
  const local = toLocal(zone, toEpochMs(at));
  const seconds = Math.floor(local.msOfDay / 1000);
  const time = `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor(seconds / 60) % 60)}:${pad(seconds % 60)}` as WallTime;
  return { date: formatLocalDate(local.days), time, offsetMinutes: local.offsetSeconds / 60 };
}

/** The instant the location's clock reads `date` at `secondsOfDay` (CD-B05 for gaps and repeated hours). */
export function instantAtWallClock(date: LocalDate, secondsOfDay: number, zone: IanaZone): bigint {
  return BigInt(resolveLocal(zone, localDateToDays(date), secondsOfDay));
}

/**
 * The first instant at or after `from` at which the location's clock reads `secondsOfDay`, looking at `date` and then
 * the day after: how a form that takes one date and several times reads a time earlier than the first one (a clock-out
 * after midnight, a break after midnight on an overnight shift, T7 FIX).
 */
export function nextWallClockAtOrAfter(from: bigint, date: LocalDate, secondsOfDay: number, zone: IanaZone): bigint {
  const sameDay = instantAtWallClock(date, secondsOfDay, zone);
  if (sameDay >= from) return sameDay;
  return instantAtWallClock(formatLocalDate(localDateToDays(date) + 1), secondsOfDay, zone);
}

